
/* ============================== src/utils/constants.js ============================== */
// path: frontend/src/utils/constants.js
/**
 * UI-справочники и словари. Источник правды для ролей/статусов/
 * приоритетов/видов замещений/лимитов файлов/доменов — GET /api/v1/meta
 * (useMetaQuery). Здесь остаются:
 *
 *   - UI-словари с русскими строками (DIALOGS, TOASTS, FILE_MESSAGES,
 *     PROJECT_TYPES, VACATION_TYPES, DEPENDENCY_TYPES, COMPANY_NAME,
 *     SYSTEM_USER) — фронт рендерит их напрямую;
 *   - белые списки разрешённых к загрузке расширений (UPLOAD_ACCEPT) и
 *     денлист MIME/расширений (DENIED_EXTENSIONS/DENIED_MIME_TYPES) —
 *     это UX-фильтр перед загрузкой; сервер валидирует независимо
 *     (domain/file-logic.ts), источник правды — он;
 *   - UI-строки WEEKLY_REMINDER_* (chip/hint/legend);
 *   - справочники по умолчанию (ROLES, TASK_STATUSES, PROJECT_STATUSES,
 *     PRIORITIES, SUBSTITUTION_KINDS) — используются только как fallback
 *     и в тех местах, где meta ещё не загрузилась; окончательный источник —
 *     meta (см. hooks/useMetaHelpers.js).
 */
export const ROLES = {
  admin: { label: "Суперадминистратор", short: "СУП", color: "#ef4444" },
  director: { label: "Генеральный директор", short: "ГД", color: "#f59e0b" },
  economist: { label: "Главный экономист", short: "ГЭ", color: "#8b5cf6" },
  accountant: { label: "Бухгалтер", short: "БУХ", color: "#16a34a" },
  kb_chief: { label: "Главный конструктор КБ", short: "ГК", color: "#0ea5e9" },
  head: { label: "Руководитель отдела", short: "РО", color: "#3b82f6" },
  project_lead: {
    label: "Ответственный по проекту",
    short: "ОП",
    color: "#ec4899",
  },
  project_manager: {
    label: "Менеджер проектов",
    short: "МП",
    color: "#f97316",
  },
  hr: { label: "HR-менеджер", short: "HR", color: "#14b8a6" },
  executor: { label: "Исполнитель", short: "ИСП", color: "#64748b" },
};

export const DELEGABLE_ROLES = Object.freeze([
  "kb_chief",
  "head",
  "project_lead",
  "project_manager",
  "hr",
  "economist",
  "accountant",
]);

export const isRoleDelegable = (role) => DELEGABLE_ROLES.includes(role);

export const TASK_STATUSES = {
  draft: { label: "Черновик", color: "#a78bfa" },
  new: { label: "Новая", color: "#94a3b8" },
  inwork: { label: "В работе", color: "#3b82f6" },
  review: { label: "На проверке", color: "#f59e0b" },
  closed: { label: "Закрыта", color: "#10b981" },
  cancelled: { label: "Отменена", color: "#64748b" },
};

export const TASK_STATUS_ORDER = [
  "new",
  "inwork",
  "review",
  "closed",
  "cancelled",
];

export const SUBSTITUTION_STATUS_VALUES = Object.freeze([
  "new",
  "inwork",
  "review",
]);

export const PRIORITIES = {
  low: { label: "Низкий", color: "#10b981" },
  mid: { label: "Средний", color: "#f59e0b" },
  high: { label: "Высокий", color: "#f97316" },
  crit: { label: "Критический", color: "#dc2626" },
};

export const PROJECT_PRIORITIES = {
  AOG: { label: "AOG", color: "#dc2626", order: 1 },
  CRIT: { label: "CRIT", color: "#f59e0b", order: 2 },
  NORM: { label: "NORM", color: "#10b981", order: 3 },
};

export const ADMIN_PROJECT_PRIORITIES = {
  high: { label: "Высокий", color: "#dc2626", order: 1 },
  mid: { label: "Средний", color: "#f59e0b", order: 2 },
  low: { label: "Низкий", color: "#10b981", order: 3 },
};

export const PRIORITY_FALLBACK_COLOR = "#64748b";

export const DEPENDENCY_TYPES = {
  FS: {
    label: "Окончание-Начало (FS)",
    desc: "Задача начнётся после завершения предыдущей",
  },
  SS: {
    label: "Начало-Начало (SS)",
    desc: "Задача начнётся одновременно с началом предыдущей",
  },
  FF: {
    label: "Окончание-Окончание (FF)",
    desc: "Задача завершится одновременно с завершением предыдущей",
  },
};

export const VACATION_TYPES = {
  annual: "Ежегодный",
  admin: "Административный",
  sick: "Больничный",
  other: "Другой",
};

export const SUBSTITUTION_KINDS = Object.freeze({
  role_share: Object.freeze({ label: "Передача ролей", delegated: false }),
  vacation: Object.freeze({ label: "Замещение на отпуск", delegated: true }),
  business_trip: Object.freeze({
    label: "Замещение на командировку",
    delegated: true,
  }),
  sick_leave: Object.freeze({
    label: "Замещение на больничный",
    delegated: true,
  }),
  rotation: Object.freeze({ label: "Замещение на ротацию", delegated: true }),
  other: Object.freeze({ label: "Замещение (другое)", delegated: true }),
});

export const SUBSTITUTION_KIND_LIST = Object.freeze(
  Object.entries(SUBSTITUTION_KINDS).map(([value, def]) => ({ value, ...def })),
);

export const substitutionKindLabel = (kind) =>
  SUBSTITUTION_KINDS[kind]?.label || kind;

export const PROJECT_STATUSES = {
  active: "Активный",
  inactive: "Неактивный",
  closed: "Закрыт",
  cancelled: "Отменён",
};

export const PROJECT_STATUS_CONFIG = {
  active: { label: "Активный", color: "#10b981" },
  inactive: { label: "Неактивный", color: "#94a3b8" },
  closed: { label: "Закрыт", color: "#3b82f6" },
  cancelled: { label: "Отменён", color: "#ef4444" },
};

export const PROJECT_STATUS_ORDER = [
  "inactive",
  "active",
  "closed",
  "cancelled",
];

export const PROJECT_TYPES = {
  prod: "Производственный",
  admin: "Административный",
};

export const SYSTEM_USER = "system";

export const COMPANY_NAME = "ООО «ВДИ»";

export const DRAFT_STATUS = Object.freeze({
  value: "draft",
  label: "Черновик",
});

const MB = 1024 * 1024;

export const FILE_LIMITS = Object.freeze({
  image: 100 * MB,
  profilePhoto: 2 * MB,
  attachment: 100 * MB,
});

export const mb = (bytes) => `${Math.round(bytes / 1024 / 1024)} МБ`;

export const FILE_MESSAGES = {
  notImage: "Можно загружать только изображения",
  imageTooLarge: `Изображение слишком большое (максимум ${mb(FILE_LIMITS.image)})`,
  profilePhotoTooLarge: `Фото слишком большое (максимум ${mb(FILE_LIMITS.profilePhoto)})`,
  attachmentTooLarge: `Файл слишком большой (максимум ${mb(FILE_LIMITS.attachment)})`,
  someImagesSkipped: `Некоторые файлы пропущены (только изображения до ${mb(FILE_LIMITS.image)})`,
  fileTypeForbidden: "Запрещено загружать файлы этого типа",
  fileTypeUndetermined: "Не удалось определить тип файла",
};

export const DENIED_EXTENSIONS = new Set([
  ".exe",
  ".dll",
  ".so",
  ".dylib",
  ".bat",
  ".cmd",
  ".com",
  ".msi",
  ".scr",
  ".ps1",
  ".sh",
  ".bash",
  ".zsh",
  ".py",
  ".rb",
  ".pl",
  ".php",
  ".jar",
  ".war",
  ".apk",
  ".app",
  ".html",
  ".htm",
  ".xhtml",
  ".svg",
  ".svgz",
  ".xml",
  ".xsl",
  ".xslt",
  ".js",
  ".mjs",
  ".cjs",
  ".jsx",
  ".ts",
  ".tsx",
  ".vbs",
  ".wsf",
  ".lnk",
  ".reg",
  ".inf",
  ".hta",
  ".scf",
  ".iso",
  ".dmg",
  ".vhd",
  ".vhdx",
]);

export const DENIED_MIME_TYPES = new Set([
  "application/x-msdownload",
  "application/x-msdos-program",
  "application/x-executable",
  "application/x-sharedlib",
  "application/x-mach-binary",
  "application/vnd.microsoft.portable-executable",
  "application/x-elf",
  "application/x-dosexec",
  "application/java-archive",
  "application/x-msi",
  "application/vnd.ms-cab-compressed",
  "text/html",
  "application/xhtml+xml",
  "image/svg+xml",
  "application/javascript",
  "text/javascript",
  "application/x-javascript",
  "application/xml",
  "text/xml",
  "application/x-shockwave-flash",
  "application/vnd.adobe.flash.movie",
]);

export const ALLOWED_IMAGE_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

export const TEXT_SAFE_EXTENSIONS = new Set([".txt"]);

export const FILE_POLICY = Object.freeze({
  limits: FILE_LIMITS,
  deniedExtensions: DENIED_EXTENSIONS,
  deniedMimeTypes: DENIED_MIME_TYPES,
  allowedImageMime: ALLOWED_IMAGE_MIME,
  textSafeExtensions: TEXT_SAFE_EXTENSIONS,
});

const ACCEPT_EXTENSIONS = Object.freeze([
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".txt",
  ".csv",
  ".md",
  ".zip",
  ".rar",
  ".7z",
  ".tar",
  ".gz",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".bmp",
  ".tiff",
  ".tif",
  ".mp3",
  ".wav",
  ".ogg",
  ".flac",
  ".mp4",
  ".mov",
  ".avi",
  ".mkv",
  ".webm",
]);

export const UPLOAD_ACCEPT = ACCEPT_EXTENSIONS.join(",");

export const FILE_ROOT_LABEL = "Корень";

export const WEEKLY_REMINDER_PERIODS = Object.freeze({
  prev_week: "Прошедшая неделя",
  prev_month: "Прошедший месяц",
  custom: "Указанный период",
});

export const WEEKLY_REMINDER_PERIOD_LIST = Object.freeze(
  Object.entries(WEEKLY_REMINDER_PERIODS).map(([value, label]) => ({
    value,
    label,
  })),
);

export const WEEKLY_REMINDER_OVERWORK_CHIP = "Сверхурочные";
export const WEEKLY_REMINDER_OVERWORK_HINT =
  "Проверить приказ о сверхурочной работе";
export const WEEKLY_REMINDER_OVERWORK_LEGEND =
  "Сверхурочные - проверить приказ о сверхурочной работе";

export const DIALOGS = {
  deleteProfilePhoto: {
    message: "Удалить фото профиля?",
    confirmLabel: "Удалить",
    danger: true,
  },
  deleteProjectPhoto: {
    message: "Удалить фото?",
    confirmLabel: "Удалить",
    danger: true,
  },
  deleteFile: {
    message: "Удалить файл?",
    confirmLabel: "Удалить",
    danger: true,
  },
  deleteNote: {
    title: "Удалить заметку",
    message: "Удалить заметку? Это действие необратимо.",
    confirmLabel: "Удалить",
    danger: true,
  },
  deleteProject: (name) => ({
    title: "Удалить проект",
    message: `Удалить проект «${name}»? Это действие необратимо.`,
    confirmLabel: "Удалить проект",
    danger: true,
  }),
  deleteTask: (title) => ({
    title: "Удалить задачу",
    message: `Удалить задачу «${title}»?`,
    confirmLabel: "Удалить",
    danger: true,
  }),
  vacationOverlap: (start, end) => ({
    message: `Исполнитель в отпуске ${start}–${end}. Продолжить?`,
    confirmLabel: "Продолжить",
  }),
  workloadOverload: (name, planHours, capacityHours, overloadHours) => ({
    title: "Превышение загрузки",
    message: `${name} будет загружен на ${Math.round(planHours)} ч из ${capacityHours} ч за период задачи. Перегрузка: ${Math.round(overloadHours)} ч. Всё равно назначить?`,
    confirmLabel: "Назначить",
  }),
  closeProject: (name) => ({
    title: "Закрыть проект",
    message: `Закрыть проект «${name}»? Все задачи будут закрыты.`,
    confirmLabel: "Закрыть",
    danger: true,
  }),
  cancelProject: (name) => ({
    title: "Отменить проект",
    message: `Отменить проект «${name}»?`,
    confirmLabel: "Отменить",
    danger: true,
  }),
  deleteTemplate: (name) => ({
    title: "Удалить шаблон",
    message: `Удалить шаблон «${name}»?`,
    confirmLabel: "Удалить",
    danger: true,
  }),
  fireEmployee: (name) => ({
    title: "Уволить сотрудника",
    message: `Уволить сотрудника «${name}»? Он переместится в архив.`,
    confirmLabel: "Уволить",
    danger: true,
  }),
  restoreEmployee: (name) => ({
    title: "Восстановить сотрудника",
    message: `Восстановить сотрудника «${name}»?`,
    confirmLabel: "Восстановить",
  }),
  createFolder: {
    title: "Новая папка",
    message: "Введите название папки",
    placeholder: "Например: Чертёж",
    confirmLabel: "Создать",
  },
  deleteFolder: (name) => ({
    title: "Удалить папку",
    message: `Удалить папку «${name}»?`,
    confirmLabel: "Удалить",
    danger: true,
  }),
  createKb: {
    title: "Новое конструкторское бюро",
    message: "Введите название КБ",
    placeholder: "Например: КБ «ЛА»",
    confirmLabel: "Создать",
  },
  createDept: {
    title: "Новый отдел",
    message: "Введите название отдела",
    placeholder: "Например: Отдел аэродинамики",
    confirmLabel: "Создать",
  },
  dailyNormExceeded: ({
    date,
    norm,
    status,
    alreadyHours,
    entryHours,
    totalAfter,
  }) => ({
    title: "Превышение дневной нормы",
    message:
      `Норма для ${date} - ${norm} ч (${status}). ` +
      (alreadyHours > 0
        ? `За этот день уже внесено ${alreadyHours} ч, ` +
          `новая запись - ${entryHours} ч, суммарно ${totalAfter} ч. `
        : `Вы вносите ${entryHours} ч. `) +
      "Переработка допустима, блокировка не выполняется - " +
      "подтвердите, чтобы сохранить.",
    confirmLabel: "Внести",
  }),
  weeklyRemindersSendNow: {
    title: "Отправить сейчас",
    message:
      "Разослать напоминания всем получателям немедленно, в обход расписания?",
    confirmLabel: "Отправить",
  },
};

export const TOASTS = {
  commentDeleted: "Комментарий удалён",
  photoDeleted: "Фото удалено",
  fileDeleted: "Файл удалён",
  fileUploaded: (name) => `Файл «${name}» загружен`,
  filesUploaded: (n) => `Загружено файлов: ${n}`,
  filesRejected: (list) => `Пропущено: ${list}`,
  projectClosed: "Проект закрыт",
  projectCancelled: "Проект отменён",
  templateDeleted: "Шаблон удалён",
  taskRestored: (title) => `Задача «${title}» восстановлена`,
  projectRestored: (name) => `Проект «${name}» восстановлен`,
  kbCreated: (name) => `КБ «${name}» создано`,
  deptCreated: (name) => `Отдел «${name}» создан`,
  folderCreated: (name) => `Папка «${name}» создана`,
  folderDeleted: "Папка удалена",
  folderNotEmpty: "Нельзя удалить непустую папку",
  weeklyReminderSaved: "Настройки напоминаний сохранены",
  weeklyRemindersSent: (n) => `Отправлено писем: ${n}`,
  weeklyReminderNoRecipients: "Получателей нет",
  weeklyReminderPeriodRequired: "Укажите даты начала и окончания периода",
  weeklyReminderPeriodInvalid: "Дата «С» не может быть позже даты «По»",
  vacationApproved: "Отпуск утверждён",
};
